import React, { useLayoutEffect, useRef } from "react";

import { useChartContext } from "../core/ChartContext";
import { useChartSubscription } from "~hooks/ticker-chart";

import type { ChartType } from "~types/types";

export interface AnimatedPriceSeriesProps {
  children: React.ReactNode;
  /** Chart area width (full reveal target) */
  width: number;
  /** Chart area height */
  height: number;
  /** Chart area x offset */
  x: number;
  /** Chart area y offset */
  y: number;
  /** Animation duration in milliseconds (default: 800) */
  duration?: number;
  /** Series ID used to check if the store has renderable data */
  seriesId: string;
  chartType: ChartType;
}

/**
 * Wraps children in an SVG clipPath that reveals content left-to-right via CSS transition.
 * Animates on mount when the store has data, and replays when the store emits a "data" event.
 * Uses a unique generated ID to avoid clipPath conflicts when multiple instances render.
 */
export function AnimatedPriceSeries({
  children,
  width,
  height,
  seriesId,
  chartType,
  x,
  y,
  duration = 800,
}: AnimatedPriceSeriesProps) {
  const clipId = `price-series-reveal-${seriesId}-${chartType}`;
  const rectRef = useRef<SVGRectElement>(null);
  const prevRevisionRef = useRef<number | null>(null);

  const store = useChartContext();
  const revision = useChartSubscription(store, "data");

  useLayoutEffect(() => {
    const rect = rectRef.current;

    if (!rect) return;

    const isNewData =
      prevRevisionRef.current === null || prevRevisionRef.current !== revision;

    prevRevisionRef.current = revision;

    if (isNewData) {
      rect.style.transition = "none";
      rect.style.width = "0px";
      rect.getBoundingClientRect();
      rect.style.transition = `width ${duration}ms ease-out`;
      rect.style.width = `${width}px`;
    } else {
      rect.style.transition = "none";
      rect.style.width = `${width}px`;
    }
  }, [width, duration, revision]);

  return (
    <g>
      <defs>
        <clipPath id={clipId}>
          <rect
            ref={rectRef}
            x={x}
            y={y}
            width={0}
            height={height}
            style={{ width: "0px" }}
          />
        </clipPath>
      </defs>
      <g clipPath={`url(#${clipId})`}>{children}</g>
    </g>
  );
}
